import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { toUiDifficulty } from '../api/difficulty';
import { toast } from '../store/useToasts';
import type { GradingPolicy, LabDetail, LabSummary, LabTask } from '../api/types';

/**
 * Lab query hooks — backed by `services/lab/handler.py`.
 *
 * The API speaks snake_case (name/estimated_minutes/task_count) and labs use
 * easy/medium/hard difficulty; these mappers translate to the camelCase view
 * types + the Difficulty enum so the UI renders real data.
 */

const num = (v: unknown, d = 0): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

function mapLabSummary(raw: any): LabSummary {
  return {
    id: String(raw?.id ?? ''),
    slug: String(raw?.slug ?? ''),
    title: String(raw?.name ?? raw?.title ?? ''),
    description: String(raw?.description ?? ''),
    scenarioSlug: String(raw?.category ?? ''),
    difficulty: toUiDifficulty(raw?.difficulty),
    estimatedMins: num(raw?.estimated_minutes ?? raw?.estimatedMins),
    taskCount: num(raw?.task_count ?? raw?.tasks?.length),
    category: String(raw?.category ?? ''),
    amiRole: String(raw?.ami_role ?? ''),
    points: num(raw?.points_possible ?? raw?.points),
  };
}

function mapLabTask(raw: any, i: number): LabTask {
  const type: LabTask['type'] = raw?.check?.type === 'flag' ? 'flag' : 'text';
  const base: LabTask = {
    id: String(raw?.id ?? i),
    title: String(raw?.title ?? `Task ${i + 1}`),
    description: String(raw?.prompt ?? raw?.description ?? ''),
    type,
    ...(raw?.points !== undefined ? { points: num(raw.points) } : {}),
  };
  return raw?.hint ? { ...base, hint: String(raw.hint) } : base;
}

function mapLabDetail(raw: any): LabDetail {
  return {
    ...mapLabSummary(raw),
    tasks: Array.isArray(raw?.tasks) ? raw.tasks.map(mapLabTask) : [],
  };
}

export const labKeys = {
  all: ['labs'] as const,
  list: () => [...labKeys.all, 'list'] as const,
  detail: (id: string) => [...labKeys.all, 'detail', id] as const,
};

export function useLabs() {
  return useQuery<ReadonlyArray<LabSummary>>({
    queryKey: labKeys.list(),
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<unknown>('/labs', { method: 'GET', signal });
      const items = Array.isArray(resp) ? resp : ((resp as { items?: unknown[] })?.items ?? []);
      return items.map(mapLabSummary);
    },
  });
}

export function useLab(id: string | undefined) {
  return useQuery<LabDetail>({
    queryKey: labKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>('/labs/{slug}', {
        method: 'GET',
        pathParams: { slug: id ?? '' },
        signal,
      });
      return mapLabDetail(raw);
    },
  });
}

/**
 * Trainee submission envelope — matches the OpenAPI `submitLabTask` operation:
 *   { enrollment_id: int, task_index: int, answer: scalar }
 *
 * The frontend tracks `labId`/`taskId` for cache invalidation but doesn't send
 * them to the server; the enrollment_id resolves the lab + tenant scope, and
 * task_index identifies the position in the lab's task array.
 */
export interface SubmitTaskInput {
  /** Cache scope only — used for `onSuccess` invalidation. */
  labId: string;
  taskId: string;
  /** Wire fields — sent to the lab handler. */
  enrollmentId: number;
  taskIndex: number;
  answer: string;
}

export interface SubmitTaskResponse {
  taskId: string;
  status: 'correct' | 'incorrect' | 'pending';
  feedback?: string;
  pointsAwarded: number;
  /** Attempts policy state after this submission (2026-09-18). */
  attemptsUsed: number;
  /** null = unlimited. */
  attemptsLeft: number | null;
  locked: boolean;
  /** What the next correct answer is worth, after a miss. */
  nextPoints: number | null;
  policy: GradingPolicy | null;
  scorePct: number | null;
}

/** Wire shape of `cmd_submit_lab_task`. */
interface SubmitTaskWire {
  is_correct: boolean | null;
  points_earned: number;
  detail?: string;
  score_pct?: number | null;
  attempts_used?: number;
  attempts_left?: number | null;
  locked?: boolean;
  next_points?: number | null;
  policy?: GradingPolicy | null;
}

function mapSubmit(taskId: string, w: SubmitTaskWire): SubmitTaskResponse {
  // The handler answers `status: 'graded' | 'submitted'` and `is_correct`;
  // the view keys on correct/incorrect/pending. Returning the wire body
  // unmapped (as this did until 2026-09-18) meant a correct answer never
  // showed the check mark — `'graded' === 'correct'` is never true.
  return {
    taskId,
    status: w.is_correct === true ? 'correct' : w.is_correct === false ? 'incorrect' : 'pending',
    ...(w.detail !== undefined ? { feedback: w.detail } : {}),
    pointsAwarded: Number(w.points_earned ?? 0),
    attemptsUsed: Number(w.attempts_used ?? 0),
    attemptsLeft: w.attempts_left === undefined ? null : w.attempts_left,
    locked: Boolean(w.locked),
    nextPoints: w.next_points ?? null,
    policy: w.policy ?? null,
    scorePct: w.score_pct ?? null,
  };
}

export function useSubmitTask() {
  const qc = useQueryClient();
  return useMutation<SubmitTaskResponse, Error, SubmitTaskInput>({
    mutationFn: async (input) => {
      // The registered route is `POST /labs/{slug}/tasks/{taskIndex}/submit`.
      // This used to post to `/lab/submit-task`, which is an operationId and
      // not a path anyone registered, so every task submission 404'd. The
      // handler reads taskIndex from the path first and the body second, so
      // both are sent and either wins.
      const wire = await apiFetch<SubmitTaskWire>('/labs/{slug}/tasks/{taskIndex}/submit', {
        method: 'POST',
        pathParams: { slug: input.labId, taskIndex: String(input.taskIndex) },
        body: {
          enrollment_id: input.enrollmentId,
          task_index: input.taskIndex,
          answer: input.answer,
        },
      });
      return mapSubmit(input.taskId, wire);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: labKeys.detail(vars.labId) });
      qc.invalidateQueries({ queryKey: ['lab-progress', vars.labId] });
    },
  });
}

export interface StartLabResponse {
  lab_slug: string;
  enrollment_id: number | null;
  range_id: number | null;
  status: string;
  execution_arn?: string | null;
}

/** Start a lab — deploys the VM + creates the enrollment (POST /labs/{slug}/start). */
export function useStartLab() {
  const qc = useQueryClient();
  return useMutation<StartLabResponse, Error, { slug: string }>({
    mutationFn: async ({ slug }) =>
      apiFetch<StartLabResponse>('/labs/{slug}/start', {
        method: 'POST',
        pathParams: { slug },
        body: { lab_slug: slug },
      }),
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: labKeys.detail(vars.slug) });
      // Optimistically flip progress to "provisioning" so useLabProgress's
      // refetchInterval starts polling IMMEDIATELY — otherwise the button only
      // switches to the running controls (Open desktop / Extend / Stop) after a
      // manual refresh. The poll then drives it to ready on its own.
      qc.setQueryData(['lab-progress', vars.slug], (old: any) => ({
        labId: vars.slug,
        tasks: [],
        percentComplete: 0,
        startedAt: null,
        completedAt: null,
        ...(old || {}),
        status: 'provisioning',
        enrollmentId: data.enrollment_id ?? undefined,
        rangeId: data.range_id ?? null,
      }));
      qc.invalidateQueries({ queryKey: ['lab-progress', vars.slug] });
    },
    // The 409 "you already have a lab running" is an expected condition that
    // labs.$id renders inline — don't double-report it as a toast.
    meta: { silentError: true },
  });
}

/** Stop a lab — tears down its range and frees the user to start another. */
export function useStopLab() {
  const qc = useQueryClient();
  return useMutation<{ status: string }, Error, { slug: string }>({
    mutationFn: async ({ slug }) =>
      apiFetch('/labs/{slug}/stop', { method: 'POST', pathParams: { slug }, body: {} }),
    onSuccess: (_data, vars) => {
      toast.success('Lab stopped', `${vars.slug} has been torn down.`);
      qc.invalidateQueries({ queryKey: ['lab-progress', vars.slug] });
      qc.invalidateQueries({ queryKey: ['my-labs'] });
    },
    meta: { errorTitle: 'Could not stop lab' },
  });
}

export interface ExtendLabResponse {
  expires_at: string;
  extend_count: number;
  max_extends: number;
  extends_remaining: number;
}

/** Extend the lab's auto-destroy by +30min (max 2 per launch). */
export function useExtendLab() {
  const qc = useQueryClient();
  return useMutation<ExtendLabResponse, Error, { slug: string }>({
    mutationFn: async ({ slug }) =>
      apiFetch<ExtendLabResponse>('/labs/{slug}/extend', { method: 'POST', pathParams: { slug }, body: {} }),
    onSuccess: (data, vars) => {
      toast.success('Lab extended by 30 minutes', `${data.extends_remaining} extend(s) remaining.`);
      qc.invalidateQueries({ queryKey: ['lab-progress', vars.slug] });
    },
    meta: { errorTitle: 'Could not extend lab' },
  });
}

export interface ActiveLab {
  enrollment_id: number;
  lab_slug: string;
  lab_name: string;
  range_id: number | null;
  status: string;
  expires_at: string | null;
  extend_count: number;
}

export interface LabProgressLite {
  status: string;
  percentComplete: number;
  completed: boolean;
}

/**
 * The caller's progress across ALL enrolled labs, keyed by lab slug — one call
 * that drives the started/completed indicator on every topic in a course.
 * Backed by `GET /me/lab-progress` (services/lab/handler.py cmd_my_lab_progress).
 */
export function useMyLabProgress() {
  return useQuery<Record<string, LabProgressLite>>({
    queryKey: ['my-lab-progress'],
    staleTime: 30_000,
    queryFn: async ({ signal }) => {
      const r = await apiFetch<{ items?: Array<{ lab_slug: string; status: string; percent_complete: number; completed: boolean }> }>(
        '/me/lab-progress',
        { method: 'GET', signal },
      );
      const map: Record<string, LabProgressLite> = {};
      for (const it of r.items ?? []) {
        map[it.lab_slug] = {
          status: it.status,
          percentComplete: Number(it.percent_complete ?? 0),
          completed: Boolean(it.completed),
        };
      }
      return map;
    },
  });
}

/** The user's one active lab (My Labs). Polls so the banner stays fresh. */
export function useMyLabs() {
  return useQuery<{ active: ActiveLab | null }>({
    queryKey: ['my-labs'],
    queryFn: async ({ signal }) =>
      apiFetch<{ active: ActiveLab | null }>('/labs/active', { method: 'GET', signal }),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}
